import { DeleteSkillDialog } from "@/components/DeleteSkillDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RESOURCE_TAG_FAVORITE, Skill } from "@cohesive/api-client";
import {
  IndexLayoutContent,
  SkillCard,
  Spinner,
  ZeroStateMotion,
} from "@cohesive/ui-components";
import { FileArchive, PenLine, Plus, ScrollText } from "lucide-react";
import React from "react";

interface SkillsIndexPageInnerProps {
  viewMode: "page" | "pane";
  isLoading: boolean;
  error: any;
  showZeroState: boolean;
  skills: Skill[];
  handleSkillAction: (skill: Skill, action: string) => void;
  handleSkillClick: (skill: Skill) => void;
  skillToDelete: Skill | null;
  handleConfirmDeleteSkill: (skill: Skill) => Promise<void>;
  setSkillToDelete: (skill: Skill | null) => void;
  onCreateSkill?: () => void;
  onUploadZip?: () => void;
}

export const SkillsIndexPageInner: React.FC<SkillsIndexPageInnerProps> = ({
  isLoading,
  error,
  showZeroState,
  skills,
  handleSkillAction,
  handleSkillClick,
  skillToDelete,
  handleConfirmDeleteSkill,
  setSkillToDelete,
  onCreateSkill,
  onUploadZip,
}) => {
  return (
    <>
      <IndexLayoutContent className="mt-4">
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertDescription>
              Failed to load skills. Please try again.
            </AlertDescription>
          </Alert>
        ) : showZeroState ? (
          <ZeroStateMotion>
            <Card className="relative bg-background/50 backdrop-blur-sm p-12 text-center max-w-md mx-auto flex flex-col items-center justify-center min-h-[400px]">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-plum/10 to-rust/10 flex items-center justify-center mb-6 mx-auto">
                <ScrollText className="h-10 w-10 text-plum" />
              </div>
              <h3 className="text-2xl font-semibold mb-2">No skills found</h3>
              <p className="text-muted-foreground mb-6">
                Add skills to enhance your agent's capabilities.
              </p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="lg"
                    className="gap-2 shadow-lg w-full sm:w-auto"
                  >
                    <Plus className="h-5 w-5" />
                    Create Skill
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center">
                  <DropdownMenuItem onClick={onCreateSkill}>
                    <PenLine className="h-4 w-4 mr-2" />
                    Create from scratch
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onUploadZip}>
                    <FileArchive className="h-4 w-4 mr-2" />
                    Upload a skill
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </Card>
          </ZeroStateMotion>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skills.map((skill) => (
              <SkillCard
                key={skill.id}
                skill={skill}
                isFavorite={!!skill.tags?.includes(RESOURCE_TAG_FAVORITE)}
                onClick={() => handleSkillClick(skill)}
                onAction={(_, action) => handleSkillAction(skill, action)}
              />
            ))}
          </div>
        )}
      </IndexLayoutContent>

      <DeleteSkillDialog
        skill={skillToDelete}
        onConfirmDelete={handleConfirmDeleteSkill}
        onCancel={() => setSkillToDelete(null)}
      />
    </>
  );
};

export default SkillsIndexPageInner;
